import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, Send, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { useAuthStore } from '../../../store/authStore';
import { useFeedbacks, useNotifications, useUsers } from '../../../hooks/useDatabase';
import { toast } from 'sonner';

export default function SupervisorFeedbackPage() {
  const { user } = useAuthStore();
  const { getUsers } = useUsers();
  const { getFeedbacksForUser, createFeedback, loading: feedbackLoading } = useFeedbacks();
  const { createNotification } = useNotifications();

  const [team, setTeam] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [ceos, setCeos] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sendText, setSendText] = useState('');
  const [sendType, setSendType] = useState('general');
  const [sendTarget, setSendTarget] = useState('admin');
  const [selectedRecipient, setSelectedRecipient] = useState('');

  useEffect(() => {
    (async () => {
      const users = await getUsers();
      setTeam(users.filter(u => u.supervisorId === user?.id));
      setAdmins(users.filter(u => u.role === 'admin'));
      setCeos(users.filter(u => u.role === 'ceo'));
    })();
  }, [getUsers, user]);

  useEffect(() => {
    (async () => {
      const list = await getFeedbacksForUser(user?.id);
      setFeedbacks(list || []);
    })();
  }, [getFeedbacksForUser, user]);

  const departments = useMemo(() => ['all', ...new Set(team.map(t => t.profile?.department).filter(Boolean))], [team]);

  const filteredRecipients = team.filter(u => {
    const matchesDept = departmentFilter === 'all' || u.profile?.department === departmentFilter;
    const matchesSearch = searchQuery === '' ||
      u.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesDept && matchesSearch;
  });

  const handleSend = async () => {
    if (sendTarget === 'employee' && !selectedRecipient) {
      toast.error('Select an employee');
      return;
    }
    if (!sendText.trim()) {
      toast.error('Feedback message is required');
      return;
    }

    const adminRecipient = admins[0];
    const ceoRecipient = ceos[0];
    const recipient = sendTarget === 'admin' ? adminRecipient : sendTarget === 'ceo' ? ceoRecipient : team.find(u => u.id === selectedRecipient);
    if (!recipient) {
      toast.error('Recipient not found');
      return;
    }

    try {
      await createFeedback({
        senderId: user?.id,
        senderRole: 'supervisor',
        receiverId: recipient.id,
        receiverRole: sendTarget,
        message: sendText.trim(),
        type: sendType,
        department: recipient.profile?.department || ''
      });

      await createNotification({
        userId: recipient.id,
        type: 'feedback_received',
        title: sendTarget === 'admin' ? 'Feedback from a supervisor' : sendTarget === 'ceo' ? 'Feedback from a supervisor' : 'Feedback from your supervisor',
        message: sendText.slice(0, 80),
        link: sendTarget === 'admin' ? '/admin/feedback' : sendTarget === 'ceo' ? '/ceo/feedback' : '/employee/feedback'
      });

      toast.success('Feedback sent');
      setSendText('');
      setSendType('general');
      setSelectedRecipient('');
      setSendTarget('employee');
    } catch (error) {
      toast.error('Failed to send feedback');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
          <p className="text-gray-500 mt-1">Respond to your team and review feedback sent to you</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Send className="w-5 h-5 text-teal-500" /> Send Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Send To</Label>
              <Select value={sendTarget} onValueChange={setSendTarget}>
                <SelectTrigger><SelectValue placeholder="Recipient" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Team Member</SelectItem>
                  <SelectItem value="ceo">CEO</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {sendTarget === 'employee' && (
              <>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept === 'all' ? 'All Departments' : dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Search Employee</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      className="pl-9"
                      placeholder="Search by name or email"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Select Employee</Label>
                  <div className="border rounded-lg max-h-52 overflow-y-auto">
                    {filteredRecipients.map(u => (
                      <button
                        key={u.id}
                        onClick={() => setSelectedRecipient(u.id)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b last:border-0 hover:bg-gray-50 ${selectedRecipient === u.id ? 'bg-teal-50' : ''}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold">
                          {u.profile?.firstName?.[0]}{u.profile?.lastName?.[0]}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{u.profile?.firstName} {u.profile?.lastName}</p>
                          <p className="text-xs text-gray-500">{u.profile?.department}</p>
                        </div>
                      </button>
                    ))}
                    {filteredRecipients.length === 0 && (
                      <div className="p-3 text-sm text-gray-500">No matching employees</div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Feedback Type</Label>
              <Select value={sendType} onValueChange={setSendType}>
                <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="appreciation">Appreciation</SelectItem>
                  <SelectItem value="issue">Issue</SelectItem>
                  <SelectItem value="suggestion">Suggestion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={sendText}
                onChange={(e) => setSendText(e.target.value)}
                placeholder="Share your feedback"
                className="min-h-28"
              />
            </div>

            <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white" onClick={handleSend} disabled={feedbackLoading}>
              <Send className="w-4 h-4 mr-2" /> {feedbackLoading ? 'Sending...' : 'Send Feedback'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5 text-violet-500" /> Feedback Received</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {feedbacks.length === 0 && (
              <div className="text-center py-10 text-gray-500">No feedback yet</div>
            )}
            {feedbacks.map(fb => (
              <div key={fb.id} className="p-4 border border-gray-100 rounded-lg">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{fb.anonymous ? 'Anonymous team member' : 'Employee'}</p>
                    {fb.department && <p className="text-xs text-gray-500">{fb.department}</p>}
                  </div>
                  <Badge variant="secondary" className="capitalize">{fb.type}</Badge>
                </div>
                <p className="text-gray-700 mt-2 whitespace-pre-line">{fb.message}</p>
                <p className="text-xs text-gray-400 mt-2">{new Date(fb.createdAt).toLocaleString()}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
